import { RefreshCw, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Alert, Button } from './ui';

export function useOnline() {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}

export function OfflineBanner() {
  const online = useOnline();
  if (online) return null;
  return (
    <div role="status" className="flex items-center justify-center gap-2 border-t border-amber-500/50 bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-950 dark:bg-amber-400/15 dark:text-amber-200">
      <WifiOff className="size-3.5" /> Offline. Saved protocols and medications are available; tools work normally.
    </div>
  );
}

/** Prompts to activate a new service worker so providers are never silently on stale content. */
export function UpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (registration) setInterval(() => registration.update(), 60 * 60 * 1000);
    },
  });
  if (!needRefresh) return null;
  return (
    <div
      role="status"
      className="fixed inset-x-3 bottom-20 z-50 mx-auto flex max-w-md items-center gap-3 rounded-md border border-stone-300 bg-white py-2.5 pl-4 pr-2.5 shadow-lg lg:bottom-6 dark:border-stone-700 dark:bg-ink-900"
    >
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-semibold">Update available</div>
        <div className="text-stone-500 dark:text-stone-400">Reload to get the latest version.</div>
      </div>
      <Button size="sm" variant="ghost" onClick={() => setNeedRefresh(false)}>
        Later
      </Button>
      <Button size="sm" icon={<RefreshCw className="size-3.5" />} onClick={() => updateServiceWorker(true)}>
        Reload
      </Button>
    </div>
  );
}

export function SampleContentNotice({ what = 'content' }: { what?: string }) {
  return (
    <Alert tone="warning" title="Sample reference content">
      This {what} is platform sample data for demonstration and has not been approved by your medical director. Always follow your
      local protocols.
    </Alert>
  );
}

export function ClinicalDisclaimer() {
  return (
    <p className="mt-8 border-t border-stone-200 pt-3 text-xs leading-relaxed text-stone-500 dark:border-stone-800">
      Clinical decision support only. Calculations must be verified against your agency’s protocols and the medication label, and do
      not replace clinical judgment or online medical control.
    </p>
  );
}
